import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarIcon, ClockIcon, ExternalLinkIcon, MapPinIcon, MessageSquareIcon, PhoneIcon, ShieldCheckIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { LOCATIONS, OPENING_HOURS, PHONE, SERVICE_MENUS } from '../data/locations';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/8ca803bb-c935-40b0-9f97-dd773b5fdb7f.jpg';
function toIntlNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? `+61${digits.slice(1)}` : digits;
}
export function Book() {
  const [searchParams] = useSearchParams();
  const initialSlug = searchParams.get('location') ?? '';
  const [slug, setSlug] = useState(LOCATIONS.some(l => l.slug === initialSlug) ? initialSlug : '');
  const [menuName, setMenuName] = useState(SERVICE_MENUS[0].name);
  const [tierDuration, setTierDuration] = useState(SERVICE_MENUS[0].tiers[0].duration);
  const location = LOCATIONS.find(l => l.slug === slug);
  const menu = SERVICE_MENUS.find(m => m.name === menuName) ?? SERVICE_MENUS[0];
  const tier = menu.tiers.find(t => t.duration === tierDuration) ?? menu.tiers[0];
  const smsBody = location ? `Hi ${location.name}, I'd like to book a ${menu.name} (${tier.duration}). Sent via the Me Time website.` : '';
  return <main className="w-full bg-ink-950" data-id="element-137" data-mp="17r32kijzha5q">
      <PageHero image={HERO_IMAGE} eyebrow="Reserve" title="Book Your Session" subtitle="Choose a location and treatment, then pick a time that suits you." icon={<CalendarIcon className="h-6 w-6" aria-hidden="true" data-id="element-139" data-mp="1cje2hleq4vya" />} data-id="element-138" data-mp="12tkpqh2uiasg" />

      <section aria-label="Booking" className="glow-warm w-full bg-ink-950" data-id="element-140" data-mp="1ck5y2w8bmtok">
        <div className="mx-auto w-full max-w-4xl space-y-6 px-5 py-20 sm:px-8" data-id="element-141" data-mp="1qjx7ejnw7lw8">
          <fieldset className="rounded-2xl border border-white/5 bg-ink-900 p-6 sm:p-8" data-id="element-142" data-mp="1lku351fqljpb">
            <legend className="px-2 text-xs font-semibold uppercase tracking-[0.24em] text-lotus-500" data-id="element-143" data-mp="j768pdwwaci9">
              Step 1 — Location
            </legend>
            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-144" data-mp="o3my0bftywwx">
              {LOCATIONS.map(loc => {
              const active = slug === loc.slug;
              return <label key={loc.slug} className={`cursor-pointer rounded-xl border p-5 transition-colors ${active ? 'border-lotus-500 bg-lotus-500/10' : 'border-white/5 bg-ink-850 hover:border-white/20'}`} data-id="element-145" data-mp="wzm5hwz7deaq">
                    <input type="radio" name="location" value={loc.slug} checked={active} onChange={() => setSlug(loc.slug)} className="sr-only" data-id="element-146" data-mp="1fxf6uuu6hg60" />
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-147" data-mp="3a5apvzvfim2c">
                      {loc.suburb}
                    </span>
                    <span className="mt-1.5 block font-bold text-white" data-id="element-148" data-mp="35c7r9dbsomfa">{loc.name}</span>
                    <span className="mt-2 flex items-start gap-2 text-sm text-neutral-400" data-id="element-149" data-mp="30cv8p4ryi1ii">
                      <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-150" data-mp="2wb9mgstj8avn" />
                      {loc.address}
                    </span>
                  </label>;
            })}
            </div>
          </fieldset>

          <fieldset className="rounded-2xl border border-white/5 bg-ink-900 p-6 sm:p-8" data-id="element-151" data-mp="3k25m2c48cadg">
            <legend className="px-2 text-xs font-semibold uppercase tracking-[0.24em] text-lotus-500" data-id="element-152" data-mp="3f4dj8n21r3uh">
              Step 2 — Treatment
            </legend>
            <div className="mt-4 space-y-5" data-id="element-153" data-mp="28p32r9aqx6pl">
              {SERVICE_MENUS.map(m => <div key={m.name} data-id="element-154" data-mp="23nds3urfqv21">
                  <p className="text-sm font-semibold text-white" data-id="element-155" data-mp="3rld9gc94ggpj">{m.name}</p>
                  <div className="mt-3 flex flex-wrap gap-3" data-id="element-156" data-mp="3rja8wyacf56k">
                    {m.tiers.map(t => {
                  const active = menuName === m.name && tierDuration === t.duration;
                  return <label key={`${m.name}-${t.duration}`} className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${active ? 'border-lotus-500 bg-lotus-500 text-white' : 'border-white/10 bg-ink-850 text-neutral-300 hover:border-white/25'}`} data-id="element-157" data-mp="3mm0lfo6il3ld">
                          <input type="radio" name="treatment" checked={active} onChange={() => {
                      setMenuName(m.name);
                      setTierDuration(t.duration);
                    }} className="sr-only" data-id="element-158" data-mp="a6pvy5xjscue" />
                          {t.duration} — {t.price}
                        </label>;
                })}
                  </div>
                </div>)}
            </div>
          </fieldset>

          <div className="rounded-2xl border border-white/5 bg-ink-900 p-6 sm:p-8" data-id="element-159" data-mp="23onhamjljcq7">
            <h2 className="text-xs font-semibold uppercase tracking-[0.24em] text-lotus-500" data-id="element-160" data-mp="1ypantiy7z7en">
              Step 3 — Pick your time
            </h2>

            {!location ? <p className="mt-4 rounded-xl border border-white/5 bg-ink-850 p-6 text-center text-sm text-neutral-400" data-id="element-161" data-mp="28mysp1pjhvaj">
                Choose a location above to see live availability.
              </p> : <motion.div key={location.slug} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="mt-5" data-id="element-162" data-mp="1fyt7kdukq4a6">
                <dl className="grid gap-3 rounded-xl border border-white/5 bg-ink-850 p-5 sm:grid-cols-3" data-id="element-163" data-mp="3pq2nk4ufi2gt">
                  <div data-id="element-164" data-mp="3trfnxzer5dgc">
                    <dt className="text-xs uppercase tracking-wider text-neutral-500" data-id="element-165" data-mp="3tqmhz0nmarz9">Venue</dt>
                    <dd className="mt-1 text-sm font-semibold text-white" data-id="element-166" data-mp="2x2gi0r1n4nrb">{location.name}</dd>
                  </div>
                  <div data-id="element-167" data-mp="321b1lv7fmgch">
                    <dt className="text-xs uppercase tracking-wider text-neutral-500" data-id="element-168" data-mp="322m2nn9ht9zw">
                      Treatment
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-white" data-id="element-169" data-mp="3b1g69xl8r0my">
                      {menu.name} · {tier.duration}
                    </dd>
                  </div>
                  <div data-id="element-170" data-mp="qayv27j49d2x">
                    <dt className="text-xs uppercase tracking-wider text-neutral-500" data-id="element-171" data-mp="qa5yfzdvg3yi">Price</dt>
                    <dd className="mt-1 text-sm font-semibold text-lotus-400" data-id="element-172" data-mp="10ffd97k2j69w">{tier.price}</dd>
                  </div>
                </dl>

                {location.squareBookingUrl ? <>
                    <a href={location.squareBookingUrl} target="_blank" rel="noopener noreferrer" className="glow-pink mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-lotus-500 px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-lotus-600" data-id="element-173" data-mp="qo14082bes3k">
                      Choose a time &amp; book
                      <ExternalLinkIcon className="h-5 w-5" aria-hidden="true" data-id="element-174" data-mp="3owj9mf2718j" />
                    </a>
                    <p className="mt-4 flex items-center justify-center gap-2 text-xs text-neutral-500" data-id="element-175" data-mp="223ziwsk6c8cp">
                      <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-176" data-mp="1y24l3y1gcd3o" />
                      Live availability and instant confirmation, powered by Square Appointments.
                    </p>
                  </> : <div className="mt-5 rounded-xl border border-white/5 bg-ink-850 p-6 text-center" data-id="element-177" data-mp="2lchhvsip5mfn">
                    <p className="text-sm text-neutral-300" data-id="element-178" data-mp="27lcgakvrqk2o">
                      Online booking is coming soon for this venue. Text or call{' '}
                      {location.name} to lock in a time — they answer during opening hours.
                    </p>
                    <div className="mt-5 flex flex-col gap-3 sm:flex-row" data-id="element-179" data-mp="22mrqlfe57ish">
                      <a href={`sms:${toIntlNumber(location.phone)}?&body=${encodeURIComponent(smsBody)}`} className="glow-pink inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-lotus-500 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-lotus-600" data-id="element-180" data-mp="1on05kup7l24z">
                        <MessageSquareIcon className="h-5 w-5" aria-hidden="true" data-id="element-181" data-mp="1ywwcy3hceg2" />
                        Text this venue
                      </a>
                      <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-orchid-400/50 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-orchid-300 transition-colors hover:bg-orchid-500/15 hover:text-white" data-id="element-182" data-mp="aqgpn4pclw3n">
                        <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-183" data-mp="tpku005y31oz" />
                        Call {location.phone}
                      </a>
                    </div>
                  </div>}
              </motion.div>}

            <p className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-500" data-id="element-184" data-mp="2vhpnhg33e71t">
              <span className="flex items-center gap-2" data-id="element-185" data-mp="2rh5qqdmfl5g7">
                <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-186" data-mp="2pfnx5par5qiz" />
                {OPENING_HOURS}
              </span>
              <span className="flex items-center gap-2" data-id="element-187" data-mp="2ld0zh7rehbdv">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-188" data-mp="2gehd568w9xkz" />
                Prefer to call?{' '}
                <a href={`tel:${(location ? location.phone : PHONE).replace(/\s/g, '')}`} className="text-lotus-500 hover:text-lotus-600" data-id="element-189" data-mp="2bg64uzy8zv4v">
                  {location ? location.phone : PHONE}
                </a>
              </span>
            </p>
          </div>
        </div>
      </section>
    </main>;
}